import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useReducer,
} from 'react';
import {
  Outlet,
  useOutletContext,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import debounce from 'lodash.debounce';
import classNames from 'classnames';

import {
  Exact,
  PatientsCount,
  usePatientsV2Query,
  usePatientsCountV2Query,
  PatientLifecycleStatusV2,
  ProgramTagDataFragment,
  MeProviderProgramTagsQuery,
  useReactivatePatientsMutation,
  useMeProviderProgramTagsWithCountQuery,
  PatientTableDataFragment,
  ProviderUserRole,
  PatientDataPermission,
  usePatientsV2LazyQuery,
  useMarkProgramSeenMutation,
  TagMatchOperator,
  ProviderUserMatchOperator,
  FilterOperator,
  useProviderUserMembersV2Query,
} from '../../../generated/graphql';

import { ColumnOrder } from '../../types/tables';

import {
  ArchiveIcon,
  PlusSmIcon,
  XIcon,
  CogIcon,
  EyeIcon,
  DownloadIcon,
} from '@heroicons/react/outline';
import Search from '../../svgs/Search';
import CloseX from '../../svgs/CloseX';

import Button from '../../components/Button';
import UpdateButton from '../../components/UpdateButton';
import InputGroup from '../../components/InputGroup';
import { MODAL_TRANSITION_DURATION } from '../../components/Modal';
import PageContainer from '../../components/Containers/PageContainer';

import AddPatientsModal from '../../components/Modals/AddPatientsModal';
import ResendInvitesModal from '../../components/Modals/ResendInvitesModal';

import {
  sortOptions,
  PatientsObject,
  getPatientsList,
  getPatientsObject,
  columnOrderToProgramSortOrder,
  getRestrictedPatientsList,
} from './helpers';
import { getNewSelectedPatients, getSelectionInterval } from './Table/helpers';
import { DEFAULT_TRANSITION_DURATION } from '../../lib/animation';
import { useGlobalModalsContext } from '../../../contexts/GlobalModalsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { AnalyticsPage } from '../../../lib/analytics';
// Removed per combined filter
import { ApolloQueryResult } from '@apollo/client';
import ToastAlert from '../../components/ToastAlert';
import toast from 'react-hot-toast';
import TagManagerModal from '../../components/Modals/TagManagerModal';
import Spinner from '../../svgs/Spinner';
import {
  PATIENT_MANAGEMENT_COLUMN_ORDER,
  PATIENT_MANAGEMENT_COLUMN_VISIBILITY,
  PATIENT_MANAGEMENT_ACTIONS_VISIBILITY,
  PATIENT_MANAGEMENT_PROVIDER_USER_FILTER,
  PATIENT_MANAGEMENT_TAG_FILTER_IDS,
  PATIENT_MANAGEMENT_EXCLUDED_TAG_FILTER_IDS,
  PATIENT_MANAGEMENT_TAGS_MATCH,
  PATIENT_MANAGEMENT_PROVIDERS_MATCH,
  PATIENT_MANAGEMENT_FILTER_OPERATOR,
  PATIENT_MANAGEMENT_NO_ASSIGNED_PRACTITIONERS,
  PATIENT_MANAGEMENT_NO_TAGS,
} from '../../lib/storage';
import useLocalStorageValue from '../../hooks/useLocalStorageValue';
import TrialEndDeactivatePatientsModal from './TrialEndDeactivatePatientsModal';
import BottomButtons from '../../components/BottomButtons';
import BulkAddTagModal from './components/BulkAddTagModal';
import BulkRemoveTagModal from './components/BulkRemoveTagModal';
import omit from 'lodash.omit';
import UpgradeModal from '../../components/Modals/UpgradeModal';

import MenuItemButton from '../../components/MenuButton';
// import { ManageTagsButton } from './ManageTagsButton';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import TableContainer from '../../components/TableContainer';
// Removed per combined filter
import { logger } from '../../../lib/logger';
import CombinedFilter from './components/CombinedFilter';
import ColumnVisibilitySettings from './components/ColumnVisibilitySettings';
import Tag from '../../svgs/Tag';
import {
  exportClients,
  generateFilename,
  downloadFile,
  ExportClientsFilterPayload,
} from '../../../api/rest/clients';
import { customToast } from '../../components/ToastAlert/customToast';
import {
  columnHeaders,
  ACTIONS_SUB_OPTIONS,
  DEFAULT_VISIBLE_ACTIONS,
} from './helpers';

type TableContext = {
  nextStepsButtonsDisabled: boolean;
  patientLifecycleStatus: PatientLifecycleStatusV2;
  hasPatientsList: boolean;
  patientsCount: PatientsCount;
  patientOrder: ColumnOrder;
  tableLoading: boolean;
  patientsList: PatientTableDataFragment[] | null;
  restrictedPatientsList: PatientTableDataFragment[] | null;
  clearFilters: () => void;
  handleSortOrderChange: (newSortOrder: ColumnOrder) => void;
  setSinglePatient: React.Dispatch<
    React.SetStateAction<PatientTableDataFragment | null>
  >;
  addPatients: () => void;
  openAssignToProgramModal: () => void;
  openResendInvitesModal: () => void;
  setIsSendAssessmentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openFirstAppointmentDateModal: () => void;
  providerProgramTags: ProgramTagDataFragment[];
  refetchMeProviderProgramTags: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<MeProviderProgramTagsQuery>>;
  handleProgramTagsUpdate: (
    programId: string,
    tags: ProgramTagDataFragment[],
  ) => void;
  handleUnarchiveClick: (programId: string) => Promise<void>;
  handleAddStorefrontClient: (programId: string) => Promise<void>;
  visibleColumns: string[];
  visibleActions: string[];
  // Bulk selection (Active clients only)
  selectedPatientsObject: PatientsObject;
  selectedPatientsList: PatientTableDataFragment[];
  handleRowCheckboxChange: (
    patient: PatientTableDataFragment,
    patientIndex: number,
    isChecked: boolean,
    isShiftKeyPressed: boolean,
  ) => void;
  handleSelectAll: (isChecked: boolean) => void;
  clearSelection: () => void;
  setLastSelectedPatientIndex: React.Dispatch<React.SetStateAction<number>>;
};

export function useTableContext() {
  return useOutletContext<TableContext>();
}

export type PatientManagementState = {
  searchTerm: string;
  tagFilter: ProgramTagDataFragment | null;
  tagFilterIds: string[];
  excludedTagFilterIds: string[];

  providerUserFilterIds: string[];

  tagsMatch: 'ANY' | 'ALL';
  providersMatch: 'ANY' | 'ALL';
  filterOperator: 'AND' | 'OR';
  noAssignedPractitioners: boolean;
  noTags: boolean;
  sortOrder: ColumnOrder;
  currentPage: number;
};

const initialPatientManagementState: PatientManagementState = {
  searchTerm: '',
  tagFilter: null,
  tagFilterIds: [],
  excludedTagFilterIds: [],
  providerUserFilterIds: [],
  tagsMatch: 'ANY',
  providersMatch: 'ANY',
  filterOperator: 'AND',
  noAssignedPractitioners: false,
  noTags: false,
  sortOrder: sortOptions.latestUnreadThenAlphabetical,
  currentPage: 1,
};

export enum PatientManagementActionType {
  SetSearchTerm = 'SetSearchTerm',
  SetTagFilter = 'SetTagFilter',
  SetTagFilterIds = 'SetTagFilterIds',
  SetExcludedTagFilterIds = 'SetExcludedTagFilterIds',
  SetProviderUserFilter = 'SetProviderUserFilter',
  SetTagsMatch = 'SetTagsMatch',
  SetProvidersMatch = 'SetProvidersMatch',
  SetFilterOperator = 'SetFilterOperator',
  SetNoAssignedPractitioners = 'SetNoAssignedPractitioners',
  SetNoTags = 'SetNoTags',
  SetSortOrder = 'SetSortOrder',
  ResetPage = 'ResetPage',
  IncrementPage = 'IncrementPage',
}

type PatientManagementAction =
  | { type: PatientManagementActionType.SetSearchTerm; payload: string }
  | {
      type: PatientManagementActionType.SetTagFilter;
      payload: ProgramTagDataFragment | null;
    }
  | {
      type: PatientManagementActionType.SetTagFilterIds;
      payload: string[];
    }
  | {
      type: PatientManagementActionType.SetExcludedTagFilterIds;
      payload: string[];
    }
  | {
      type: PatientManagementActionType.SetProviderUserFilter;
      payload: string[];
    }
  | {
      type: PatientManagementActionType.SetTagsMatch;
      payload: 'ANY' | 'ALL';
    }
  | {
      type: PatientManagementActionType.SetProvidersMatch;
      payload: 'ANY' | 'ALL';
    }
  | {
      type: PatientManagementActionType.SetFilterOperator;
      payload: 'AND' | 'OR';
    }
  | {
      type: PatientManagementActionType.SetNoAssignedPractitioners;
      payload: boolean;
    }
  | {
      type: PatientManagementActionType.SetNoTags;
      payload: boolean;
    }
  | {
      type: PatientManagementActionType.SetSortOrder;
      payload: ColumnOrder;
    }
  | {
      type: PatientManagementActionType.ResetPage;
      payload?: Partial<PatientManagementState>;
    }
  | { type: PatientManagementActionType.IncrementPage };

const patientManagementReducer = (
  state: PatientManagementState,
  action: PatientManagementAction,
): PatientManagementState => {
  switch (action.type) {
    case PatientManagementActionType.SetSearchTerm:
      return {
        ...state,
        searchTerm: action.payload,
        currentPage: 1,
      };
    case PatientManagementActionType.SetTagFilter:
      return {
        ...state,
        tagFilter: action.payload,
        currentPage: 1,
      };
    case PatientManagementActionType.SetTagFilterIds:
      return {
        ...state,
        tagFilterIds: action.payload,
        currentPage: 1,
      };
    case PatientManagementActionType.SetExcludedTagFilterIds:
      return {
        ...state,
        excludedTagFilterIds: action.payload,
        currentPage: 1,
      };

    case PatientManagementActionType.SetProviderUserFilter:
      return {
        ...state,
        providerUserFilterIds: action.payload,
        currentPage: 1,
      };

    case PatientManagementActionType.SetTagsMatch:
      return { ...state, tagsMatch: action.payload, currentPage: 1 };
    case PatientManagementActionType.SetProvidersMatch:
      return { ...state, providersMatch: action.payload, currentPage: 1 };
    case PatientManagementActionType.SetFilterOperator:
      return { ...state, filterOperator: action.payload, currentPage: 1 };
    case PatientManagementActionType.SetNoAssignedPractitioners:
      return {
        ...state,
        noAssignedPractitioners: action.payload,
        currentPage: 1,
      };
    case PatientManagementActionType.SetNoTags:
      return { ...state, noTags: action.payload, currentPage: 1 };
    case PatientManagementActionType.SetSortOrder:
      return {
        ...state,
        sortOrder: action.payload,
        currentPage: 1,
      };
    case PatientManagementActionType.ResetPage:
      return {
        ...state,
        searchTerm: '',
        tagFilter: null,
        tagFilterIds: [],
        excludedTagFilterIds: [],
        providerUserFilterIds: [],
        tagsMatch: 'ANY',
        providersMatch: 'ANY',
        filterOperator: 'AND',
        noAssignedPractitioners: false,
        noTags: false,
        sortOrder: sortOptions.latestUnreadThenAlphabetical,
        currentPage: 1,
        ...(action.payload || {}),
      };
    case PatientManagementActionType.IncrementPage:
      return { ...state, currentPage: state.currentPage + 1 };
    default:
      return state;
  }
};

// TagFilterOption removed – handled inside CombinedFilter

export const PATIENTS_ACTIVE_BASE_PATH = '/clients/active';
export const PATIENTS_ARCHIVED_BASE_PATH = '/clients/archived';

export const PATIENT_PAGE_SIZE = 20;
export const PAGINATION_DEBOUNCE_DELAY = 500;
export const INFINITE_SCROLL_LOADER_MARGIN = '96px';

const PatientManagement = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    showUpgradeBanner,
    authedProviderUser,
    willHitClientLimit,
    refreshAuthedProviderUserSubscriptionState,
  } = useAuth();
  const { showUpgradeModal } = useGlobalModalsContext();

  const noPatientDataPermission =
    authedProviderUser.patientDataPermission === PatientDataPermission.None;

  // patientsCount query moved below to have state available

  const [reactivatePatientsMutation] = useReactivatePatientsMutation();
  const [markProgramSeenMutation] = useMarkProgramSeenMutation();

  // Provider-level Program Tags (for filter menu)

  const {
    data: meProviderProgramTagsData,
    refetch: refetchMeProviderProgramTags,
  } = useMeProviderProgramTagsWithCountQuery();

  const providerProgramTags =
    meProviderProgramTagsData?.meProvider?.provider?.programTags ?? [];

  const [isAddPatientsModalOpen, setIsAddPatientsModalOpen] = useState(false);
  const [isResendInvitesModalOpen, setIsResendInvitesModalOpen] =
    useState(false);
  const [isSendAssessmentModalOpen, setIsSendAssessmentModalOpen] =
    useState(false);
  const [isTagManagerModalOpen, setIsTagManagerModalOpen] = useState(false);
  const [isBlockingUpgradeModalOpen, setIsBlockingUpgradeModalOpen] =
    useState(false);
  const [
    isTrialEndDeactivatePatientsModalOpen,
    setIsTrialEndDeactivatePatientsModalOpen,
  ] = useState(false);
  const [isBulkAddTagModalOpen, setIsBulkAddTagModalOpen] = useState(false);
  const [isBulkRemoveTagModalOpen, setIsBulkRemoveTagModalOpen] =
    useState(false);

  // Enforces client downgrade flow
  useEffect(() => {
    if (willHitClientLimit(0)) {
      // Client limit is surpassed
      setIsBlockingUpgradeModalOpen(true);
    } else {
      setIsBlockingUpgradeModalOpen(false);
    }
  }, [willHitClientLimit]);

  // cleanup function for lingering single patient state
  useEffect(() => {
    if (!isResendInvitesModalOpen && !isSendAssessmentModalOpen) {
      setTimeout(() => {
        setSinglePatient(null);
      }, MODAL_TRANSITION_DURATION);
    }
  }, [isResendInvitesModalOpen, isSendAssessmentModalOpen]);

  // Patient lifecycle status / routing

  let patientLifecycleStatus: PatientLifecycleStatusV2 | null = null;
  if (pathname.includes(PATIENTS_ACTIVE_BASE_PATH)) {
    patientLifecycleStatus = PatientLifecycleStatusV2.Active;
  } else if (pathname.includes(PATIENTS_ARCHIVED_BASE_PATH)) {
    patientLifecycleStatus = PatientLifecycleStatusV2.Archived;
  }

  const isInActiveMode =
    patientLifecycleStatus === PatientLifecycleStatusV2.Active;
  const isInArchivedMode =
    patientLifecycleStatus === PatientLifecycleStatusV2.Archived;

  // Refetch counts on lifecycle status change is handled by Apollo variables change below

  // Search, filters, ordering

  const [storedColumnOrder, setStoredColumnOrder] =
    useLocalStorageValue<ColumnOrder>(
      PATIENT_MANAGEMENT_COLUMN_ORDER,
      authedProviderUser.id,
      authedProviderUser.provider.id,
    );

  // Initialize column visibility from localStorage or default to all columns
  const [storedColumnVisibility, setStoredColumnVisibility] =
    useLocalStorageValue<string[]>(
      PATIENT_MANAGEMENT_COLUMN_VISIBILITY,
      authedProviderUser?.id,
      authedProviderUser?.provider?.id,
    );

  // Actions sub-visibility (Chat, Follow-up, Private notes) within the Actions column
  const [storedVisibleActions, setStoredVisibleActions] = useLocalStorageValue<
    string[]
  >(
    PATIENT_MANAGEMENT_ACTIONS_VISIBILITY,
    authedProviderUser?.id,
    authedProviderUser?.provider?.id,
  );
  // Migrate visibleActions from legacy column visibility (actionChat, etc.)
  const visibleActions = (() => {
    if (storedVisibleActions) return storedVisibleActions;
    const stored = storedColumnVisibility;
    if (!stored) return DEFAULT_VISIBLE_ACTIONS;
    const legacyFromColumns = [
      'actionChat',
      'actionFollowUp',
      'actionNotes',
    ].filter((f) => stored.includes(f));
    return legacyFromColumns.length > 0
      ? legacyFromColumns
      : DEFAULT_VISIBLE_ACTIONS;
  })();

  // Get all available column fields for default value
  const allAvailableColumns = Object.values(columnHeaders)
    .flat()
    .map((col) => col.field);

  // New columns hidden by default; users can enable via Column visibility
  const defaultHiddenColumns = ['programStartDate', 'programEndDate'];
  const defaultVisibleColumns = allAvailableColumns.filter(
    (col) => !defaultHiddenColumns.includes(col),
  );

  // Use stored visibility or default (new columns hidden)
  // Migrate legacy actionChat/actionFollowUp/actionNotes to sendFollowUp + visibleActions
  const visibleColumns = (() => {
    const base = storedColumnVisibility || defaultVisibleColumns;
    if (!storedColumnVisibility) return base;
    const legacyActionFields = ['actionChat', 'actionFollowUp', 'actionNotes'];
    const hasLegacyActions = legacyActionFields.some((f) => base.includes(f));
    if (!hasLegacyActions) return base;
    const migrated = base
      .filter((col) => !legacyActionFields.includes(col))
      .concat(['sendFollowUp']);
    return Array.from(new Set(migrated));
  })();

  // Backward compatible: previously stored as string or null; now an array of ids
  const [storedProviderUserFilter, setStoredProviderUserFilter] =
    useLocalStorageValue<string[] | string | null>(
      PATIENT_MANAGEMENT_PROVIDER_USER_FILTER,
      authedProviderUser.id,
      authedProviderUser.provider.id,
    );

  // Provider-scoped storage helpers for other filter dimensions
  const setLocalStorageValue = <T,>(key: string, value: T) => {
    try {
      const scopedKey = `${authedProviderUser.id}:${authedProviderUser.provider.id}:${key}`;
      localStorage.setItem(scopedKey, JSON.stringify(value));
    } catch (e) {
      // noop
    }
  };
  const getLocalStorageValue = <T,>(key: string): T | null => {
    try {
      const scopedKey = `${authedProviderUser.id}:${authedProviderUser.provider.id}:${key}`;
      const raw = localStorage.getItem(scopedKey);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  };
  const removeLocalStorageKey = (key: string) => {
    try {
      const scopedKey = `${authedProviderUser.id}:${authedProviderUser.provider.id}:${key}`;
      localStorage.removeItem(scopedKey);
    } catch (e) {
      // noop
    }
  };
  const initialProviderFilterIds: string[] = Array.isArray(
    storedProviderUserFilter,
  )
    ? (storedProviderUserFilter as string[])
    : storedProviderUserFilter
    ? [storedProviderUserFilter as string]
    : [];

  const [patientManagementState, dispatch] = useReducer(
    patientManagementReducer,
    {
      ...initialPatientManagementState,
      ...(storedColumnOrder && { sortOrder: storedColumnOrder }),
      ...(initialProviderFilterIds.length > 0 && {
        providerUserFilterIds: initialProviderFilterIds,
      }),
      // Rehydrate other persisted filters
      ...(getLocalStorageValue<string[]>(PATIENT_MANAGEMENT_TAG_FILTER_IDS) && {
        tagFilterIds: getLocalStorageValue<string[]>(
          PATIENT_MANAGEMENT_TAG_FILTER_IDS,
        )!,
      }),
      ...(getLocalStorageValue<string[]>(
        PATIENT_MANAGEMENT_EXCLUDED_TAG_FILTER_IDS,
      ) && {
        excludedTagFilterIds: getLocalStorageValue<string[]>(
          PATIENT_MANAGEMENT_EXCLUDED_TAG_FILTER_IDS,
        )!,
      }),

      ...(getLocalStorageValue<'ANY' | 'ALL'>(
        PATIENT_MANAGEMENT_TAGS_MATCH,
      ) && {
        tagsMatch: getLocalStorageValue<'ANY' | 'ALL'>(
          PATIENT_MANAGEMENT_TAGS_MATCH,
        )!,
      }),
      ...(getLocalStorageValue<'ANY' | 'ALL'>(
        PATIENT_MANAGEMENT_PROVIDERS_MATCH,
      ) && {
        providersMatch: getLocalStorageValue<'ANY' | 'ALL'>(
          PATIENT_MANAGEMENT_PROVIDERS_MATCH,
        )!,
      }),
      ...(getLocalStorageValue<'AND' | 'OR'>(
        PATIENT_MANAGEMENT_FILTER_OPERATOR,
      ) && {
        filterOperator: getLocalStorageValue<'AND' | 'OR'>(
          PATIENT_MANAGEMENT_FILTER_OPERATOR,
        )!,
      }),
      ...(getLocalStorageValue<boolean>(
        PATIENT_MANAGEMENT_NO_ASSIGNED_PRACTITIONERS,
      ) && {
        noAssignedPractitioners: getLocalStorageValue<boolean>(
          PATIENT_MANAGEMENT_NO_ASSIGNED_PRACTITIONERS,
        )!,
      }),
      ...(getLocalStorageValue<boolean>(PATIENT_MANAGEMENT_NO_TAGS) && {
        noTags: getLocalStorageValue<boolean>(PATIENT_MANAGEMENT_NO_TAGS)!,
      }),
    },
  );

  const {
    searchTerm,
    tagFilterIds,
    excludedTagFilterIds,
    providerUserFilterIds,
    tagsMatch,
    providersMatch,
    filterOperator,
    noAssignedPractitioners,
    noTags,
    sortOrder,
    currentPage,
  } = patientManagementState;

  const clientsListFilter = {
    programTagIds: tagFilterIds,
    excludedProgramTagIds: excludedTagFilterIds,
    assignedProviderUserIds: providerUserFilterIds,
    tagsMatch:
      tagsMatch === 'ALL' ? TagMatchOperator.All : TagMatchOperator.Any,
    assignedProviderUsersMatch:
      providersMatch === 'ALL'
        ? ProviderUserMatchOperator.All
        : ProviderUserMatchOperator.Any,
    filterOperator:
      filterOperator === 'AND' ? FilterOperator.And : FilterOperator.Or,
    no_assigned_practitioners: noAssignedPractitioners,
    no_tags: noTags,
  };

  // Unfiltered active/archived totals so the chip stays "Showing y of x"
  const { data: patientsCount, refetch: refetchPatientsCount } =
    usePatientsCountV2Query();

  // Load full team list so the filter always shows all members (not only those in the current patient page)
  const { data: providerMembersData } = useProviderUserMembersV2Query({
    variables: { activeProviderUsersOnly: true },
    fetchPolicy: 'cache-first',
  });
  const availableProviderUsersFromTeam = (
    providerMembersData?.providerUserMembers || []
  ).map((u) => ({
    id: u.id,
    name: u.name,
    email: u.email,
    isExternalCollaborator: u.isExternalCollaborator,
    profileImageMedia: u.profileImageMedia
      ? { url: u.profileImageMedia.url }
      : null,
  }));

  const [searchTermValue, setSearchTermValue] = useState('');
  const debouncedSearch = useCallback(
    debounce(
      (e) =>
        dispatch({
          type: PatientManagementActionType.SetSearchTerm,
          payload: e.target.value.toLowerCase().trim(),
        }),
      DEFAULT_TRANSITION_DURATION,
    ),
    [],
  );

  // const hasTagFilter = tagFilter !== null;

  const programSortOrder = columnOrderToProgramSortOrder(sortOrder);

  const hasAnyFiltersApplied =
    providerUserFilterIds.length > 0 ||
    tagFilterIds.length > 0 ||
    excludedTagFilterIds.length > 0 ||
    noAssignedPractitioners ||
    noTags;

  // activeCountLabel computed after patient lists are available below

  const handleSortOrderChange = (newSortOrder: ColumnOrder) => {
    setStoredColumnOrder(newSortOrder);
    dispatch({
      type: PatientManagementActionType.SetSortOrder,
      payload: newSortOrder,
    });
  };

  const clearFilters = (stateOverride?: Partial<PatientManagementState>) => {
    setSearchTermValue('');
    // Clear persisted provider-user filter as well so refresh doesn't restore it
    setStoredProviderUserFilter([]);
    // Clear other persisted filters
    removeLocalStorageKey(PATIENT_MANAGEMENT_TAG_FILTER_IDS);
    removeLocalStorageKey(PATIENT_MANAGEMENT_EXCLUDED_TAG_FILTER_IDS);
    removeLocalStorageKey(PATIENT_MANAGEMENT_TAGS_MATCH);
    removeLocalStorageKey(PATIENT_MANAGEMENT_PROVIDERS_MATCH);
    removeLocalStorageKey(PATIENT_MANAGEMENT_FILTER_OPERATOR);
    removeLocalStorageKey(PATIENT_MANAGEMENT_NO_ASSIGNED_PRACTITIONERS);
    removeLocalStorageKey(PATIENT_MANAGEMENT_NO_TAGS);
    dispatch({
      type: PatientManagementActionType.ResetPage,
      payload: stateOverride,
    });
  };

  // Clear only filters (keep search as-is)
  const handleClearFilters = () => {
    clearSelection();
    setStoredProviderUserFilter([]);
    removeLocalStorageKey(PATIENT_MANAGEMENT_TAG_FILTER_IDS);
    removeLocalStorageKey(PATIENT_MANAGEMENT_EXCLUDED_TAG_FILTER_IDS);
    removeLocalStorageKey(PATIENT_MANAGEMENT_TAGS_MATCH);
    removeLocalStorageKey(PATIENT_MANAGEMENT_PROVIDERS_MATCH);
    removeLocalStorageKey(PATIENT_MANAGEMENT_FILTER_OPERATOR);
    removeLocalStorageKey(PATIENT_MANAGEMENT_NO_ASSIGNED_PRACTITIONERS);
    removeLocalStorageKey(PATIENT_MANAGEMENT_NO_TAGS);
    dispatch({
      type: PatientManagementActionType.ResetPage,
      payload: { searchTerm },
    });
  };

  const handleActiveClick: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.stopPropagation();
    if (!isInActiveMode) {
      navigate(PATIENTS_ACTIVE_BASE_PATH);
    }
  };

  const handleArchivedClick: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.stopPropagation();
    if (!isInArchivedMode) {
      clearSelection();
      navigate(PATIENTS_ARCHIVED_BASE_PATH);
    }
  };

  const handleCombinedFilterApply = (selection: {
    providerUserIds: string[];
    tagIds: string[];
    excludedTagIds: string[];
    tagsMatch: 'ANY' | 'ALL';
    providersMatch: 'ANY' | 'ALL';
    filterOperator: 'AND' | 'OR';
    no_assigned_practitioners?: boolean;
    no_tags?: boolean;
  }) => {
    clearSelection();
    // Persist all filter dimensions per provider
    setStoredProviderUserFilter(selection.providerUserIds);
    // Keep local tag selection in state for chips, but server now supports multi-tags via filter
    const firstTag =
      providerProgramTags.find((t) => selection.tagIds.includes(t.id)) ?? null;
    dispatch({
      type: PatientManagementActionType.SetTagFilter,
      payload: firstTag || null,
    });
    dispatch({
      type: PatientManagementActionType.SetTagFilterIds,
      payload: selection.tagIds,
    });
    dispatch({
      type: PatientManagementActionType.SetExcludedTagFilterIds,
      payload: selection.excludedTagIds,
    });
    dispatch({
      type: PatientManagementActionType.SetProviderUserFilter,
      payload: selection.providerUserIds,
    });
    dispatch({
      type: PatientManagementActionType.SetTagsMatch,
      payload: selection.tagsMatch,
    });
    dispatch({
      type: PatientManagementActionType.SetProvidersMatch,
      payload: selection.providersMatch,
    });
    dispatch({
      type: PatientManagementActionType.SetFilterOperator,
      payload: selection.filterOperator,
    });
    dispatch({
      type: PatientManagementActionType.SetNoAssignedPractitioners,
      payload: selection.no_assigned_practitioners || false,
    });
    dispatch({
      type: PatientManagementActionType.SetNoTags,
      payload: selection.no_tags || false,
    });
    setLocalStorageValue(PATIENT_MANAGEMENT_TAG_FILTER_IDS, selection.tagIds);
    setLocalStorageValue(
      PATIENT_MANAGEMENT_EXCLUDED_TAG_FILTER_IDS,
      selection.excludedTagIds,
    );
    setLocalStorageValue(PATIENT_MANAGEMENT_TAGS_MATCH, selection.tagsMatch);
    setLocalStorageValue(
      PATIENT_MANAGEMENT_PROVIDERS_MATCH,
      selection.providersMatch,
    );
    setLocalStorageValue(
      PATIENT_MANAGEMENT_FILTER_OPERATOR,
      selection.filterOperator,
    );
    setLocalStorageValue(
      PATIENT_MANAGEMENT_NO_ASSIGNED_PRACTITIONERS,
      selection.no_assigned_practitioners || false,
    );
    setLocalStorageValue(
      PATIENT_MANAGEMENT_NO_TAGS,
      selection.no_tags || false,
    );
  };

  const handleColumnVisibilityChange = (visibleColumns: string[]) => {
    setStoredColumnVisibility(visibleColumns);
  };

  const handleVisibleActionsChange = (visibleActions: string[]) => {
    setStoredVisibleActions(visibleActions);
  };

  // Expose Column modal open to Settings menu
  const [setColumnVisibilityOpen, setSetColumnVisibilityOpen] = useState<
    (() => void) | null
  >(null);

  const handleExportFromMenu = async () => {
    try {
      const filterPayload: ExportClientsFilterPayload = {
        programTagIds: tagFilterIds,
        excludedProgramTagIds: excludedTagFilterIds,
        assignedProviderUserIds: providerUserFilterIds,
        tagsMatch: tagsMatch,
        assignedProviderUsersMatch: providersMatch,
        filterOperator: filterOperator,
      };

      const response = await exportClients({
        patientLifecycleStatuses: patientLifecycleStatus
          ? [patientLifecycleStatus]
          : undefined,
        filter: filterPayload,
      });
      const filename = generateFilename();
      downloadFile(response, filename);
    } catch (error) {
      customToast.error('Something went wrong.');
    }
  };

  const { loading: tableLoading, refetch: refetchPatientsTable } =
    usePatientsV2Query({
      variables: {
        patientLifecycleStatuses: [patientLifecycleStatus!],
        searchQuery: searchTerm,
        filter: clientsListFilter,
        sortOrder: programSortOrder,
        pageSize: PATIENT_PAGE_SIZE,
        page: 1,
      },
      skip: !patientLifecycleStatus,
      onCompleted: (data) => {
        if (data) {
          const { patientsV2 } = data;
          firstPageLoadedRef.current = true;
          endReachedRef.current = patientsV2.length < PATIENT_PAGE_SIZE;
          setPatientsObject(getPatientsObject(patientsV2));
        }
      },
    });

  const [fetchMorePatients, { loading: morePatientsLoading }] =
    usePatientsV2LazyQuery({
      variables: {
        patientLifecycleStatuses: [patientLifecycleStatus!],
        searchQuery: searchTerm,
        filter: clientsListFilter,
        sortOrder: programSortOrder,
        pageSize: PATIENT_PAGE_SIZE,
        page: currentPage,
      },
      fetchPolicy: 'network-only', // Ensure fresh data is fetched
    });

  // Found these necessary to properly control race conditions. There could potentially be other
  // ways, and I wish I didn't need two refs, but due to the async and split nature of the two patient
  // loading calls, I couldn't guard against edge cases well enough without them.
  const firstPageLoadedRef = useRef(false);
  const endReachedRef = useRef(false);

  const handleLoadMorePatients = async () => {
    if (endReachedRef.current || morePatientsLoading || tableLoading) {
      logger.debug('Skipping load more - already loading or end reached');
      return;
    }

    if (endReachedRef.current) return;

    dispatch({ type: PatientManagementActionType.IncrementPage });

    const { data } = await fetchMorePatients();
    if (data) {
      const paginationEndReached = data.patientsV2.length < PATIENT_PAGE_SIZE;
      logger.debug(`Fetched ${data.patientsV2.length} more patients`);

      if (paginationEndReached) {
        endReachedRef.current = true;
      }

      setPatientsObject((prevPatientsObject) => ({
        ...prevPatientsObject,
        ...getPatientsObject(data.patientsV2),
      }));
    }
  };

  const debouncedHandleLoadMorePatients = useCallback(
    debounce(handleLoadMorePatients, PAGINATION_DEBOUNCE_DELAY, {
      leading: true,
      trailing: false,
    }),
    [
      currentPage,
      endReachedRef.current,
      tableLoading,
      morePatientsLoading,
      fetchMorePatients,
    ],
  );

  const observer = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const handleIntersection = async (entries: IntersectionObserverEntry[]) => {
    const sentinelEntry = entries[0];

    if (
      sentinelEntry.isIntersecting &&
      firstPageLoadedRef.current &&
      !tableLoading &&
      !morePatientsLoading
    ) {
      logger.debug('Loading more patients from intersection observer');
      debouncedHandleLoadMorePatients();
    }
  };

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(handleIntersection, {
      rootMargin: INFINITE_SCROLL_LOADER_MARGIN,
    });

    if (sentinelRef.current) {
      observer.current.observe(sentinelRef.current);
    }

    return () => {
      observer.current?.disconnect();
    };
  }, [tableLoading, morePatientsLoading]);

  const [patientsObject, setPatientsObject] = useState<PatientsObject | null>(
    null,
  );
  const patientsList = patientsObject
    ? getPatientsList(patientsObject, authedProviderUser?.hasPremiumAccess)
    : null;
  const restrictedPatientsList = patientsObject
    ? getRestrictedPatientsList(
        patientsObject,
        authedProviderUser?.hasPremiumAccess,
      )
    : null;

  // Server-side filtering; no client-side filtering needed
  const filteredPatientsList = patientsList;
  const filteredRestrictedPatientsList = restrictedPatientsList;

  const hasPatientsList =
    filteredPatientsList && filteredPatientsList?.length > 0 && !tableLoading;

  const activeCountLabel = (() => {
    const total = patientsCount?.activePatientsCount ?? 0;
    const showing = filteredPatientsList?.length ?? 0;
    return hasAnyFiltersApplied && isInActiveMode
      ? `Showing ${showing} of ${total}`
      : `${total}`;
  })();

  const archivedCountLabel = (() => {
    const total = patientsCount?.archivedPatientsCount ?? 0;
    const showing = filteredPatientsList?.length ?? 0;
    return hasAnyFiltersApplied && isInArchivedMode
      ? `Showing ${showing} of ${total}`
      : `${total}`;
  })();

  const [singlePatient, setSinglePatient] =
    useState<PatientTableDataFragment | null>(null);

  const [selectedPatientsObject, setSelectedPatientsObject] =
    useState<PatientsObject>({});
  const [lastSelectedPatientIndex, setLastSelectedPatientIndex] =
    useState<number>(-1);

  const selectedPatientsList = React.useMemo(
    () => Object.values(selectedPatientsObject),
    [selectedPatientsObject],
  );

  const hasTagsToRemove = React.useMemo(() => {
    const tagIds = new Set<string>();
    for (const patient of selectedPatientsList) {
      for (const tag of patient.tags ?? []) {
        tagIds.add(tag.id);
      }
    }
    return tagIds.size > 0;
  }, [selectedPatientsList]);

  const clearSelection = useCallback(() => {
    setSelectedPatientsObject({});
    setLastSelectedPatientIndex(-1);
  }, []);

  const handleRowCheckboxChange = useCallback(
    (
      patient: PatientTableDataFragment,
      patientIndex: number,
      isChecked: boolean,
      isShiftKeyPressed: boolean,
    ) => {
      const patientsList = filteredPatientsList ?? [];
      const selectionInterval = getSelectionInterval(
        patientIndex,
        lastSelectedPatientIndex,
        patientsList,
        isShiftKeyPressed,
      );
      const newSelection = getNewSelectedPatients(
        isChecked,
        selectionInterval,
        patient,
        selectedPatientsObject,
        isShiftKeyPressed,
        selectedPatientsList,
      );
      setSelectedPatientsObject(newSelection);
      setLastSelectedPatientIndex(patientIndex);
    },
    [
      filteredPatientsList,
      lastSelectedPatientIndex,
      selectedPatientsObject,
      selectedPatientsList,
    ],
  );

  const handleSelectAll = useCallback(
    (isChecked: boolean) => {
      const patientsList = filteredPatientsList ?? [];
      if (isChecked) {
        setSelectedPatientsObject(getPatientsObject(patientsList));
      } else {
        setSelectedPatientsObject({});
      }
      setLastSelectedPatientIndex(-1);
    },
    [filteredPatientsList],
  );

  const addPatients = () => {
    if (willHitClientLimit()) {
      showUpgradeModal(AnalyticsPage.ClientsTable);
      return;
    }
    setIsAddPatientsModalOpen(true);
  };

  const handleProgramTagsUpdate = (
    programId: string,
    tags: ProgramTagDataFragment[],
  ) => {
    const updatedPatientsObject = {
      ...patientsObject,
      [programId]: {
        ...patientsObject[programId],
        tags,
      },
    };
    setPatientsObject(updatedPatientsObject);

    if (programId in selectedPatientsObject) {
      setSelectedPatientsObject((prev) => ({
        ...prev,
        [programId]: {
          ...prev[programId],
          tags,
        },
      }));
    }
  };

  const handleUnarchiveClick = async (programId: string) => {
    if (willHitClientLimit()) {
      showUpgradeModal(AnalyticsPage.ClientsTable);
      return;
    }
    try {
      const response = await reactivatePatientsMutation({
        variables: {
          input: {
            programIds: [programId],
          },
        },
      });

      if (response.data?.reactivatePatients.reactivatedPatients?.length) {
        await refetchPatientsCount();
        await refreshAuthedProviderUserSubscriptionState();
        setPatientsObject((prevPatientsObject) => {
          const { [programId]: _, ...rest } = prevPatientsObject;
          return rest;
        });
      }
    } catch (error) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to unarchive client."
          level="error"
        />
      ));
    }
  };

  const handleAddStorefrontClient = async (programId: string) => {
    // It's a bit hacky, but new storefront clients are categorized as such and restricted
    // based on their "last seen" date. The only other place where the client is marked "seen"
    // is when visiting their profile page, and these clients are blocked from being accessed
    // on the back-end, preventing that.
    // Therefore, marking the program as seen here "accepts" the client, claiming a client seat
    // and making them accessible.
    try {
      const response = await markProgramSeenMutation({
        variables: {
          programId,
        },
      });
      if (response.data?.markProgramSeen) {
        const updatedPatient = response.data.markProgramSeen;
        setPatientsObject({
          ...patientsObject,
          [programId]: {
            ...updatedPatient,
          },
        });
      }
      // Refresh the subscription state to update the client seat count
      await refreshAuthedProviderUserSubscriptionState();
    } catch (error) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to add client."
          level="error"
        />
      ));
    }
  };

  const handlePostDeactivation = async (deactivatedProgramIds: string[]) => {
    await refetchPatientsCount();
    await refreshAuthedProviderUserSubscriptionState();
    const remainingPatients = omit(patientsObject, deactivatedProgramIds);
    setPatientsObject(remainingPatients);
    clearFilters();
  };

  const shouldShowPage = patientsCount;

  const showManageTags =
    patientLifecycleStatus !== PatientLifecycleStatusV2.Archived &&
    authedProviderUser.role !== ProviderUserRole.Practitioner;

  const showExportClients =
    authedProviderUser.patientDataPermission !== PatientDataPermission.None;

  // Settings menu now contains Tags/Export; this component is unused

  return (
    <PageContainer
      containerClassName={classNames(
        showUpgradeBanner
          ? 'h-[calc(100vh-var(--top-nav-height)-var(--upgrade-banner-height))] overflow-y-hidden'
          : 'h-[calc(100vh-var(--top-nav-height))] overflow-y-hidden',
      )}
    >
      <div
        className={classNames(
          'h-full transition duration-300',
          !shouldShowPage && 'opacity-0',
          shouldShowPage && 'opacity-100',
        )}
      >
        <div className="mb-4 flex h-[48px] flex-row justify-between">
          <div className="flex flex-row items-center">
            <div className="font-serif text-subtitle text-green-150">
              {isInArchivedMode ? 'Archived clients' : 'Clients'}
            </div>
          </div>
          <div className="flex flex-row items-center gap-x-5">
            {!authedProviderUser.hasPremiumAccess && (
              <div className="flex items-center gap-x-2">
                <div className="text-small-caption text-neutral-110">
                  Available client seats
                </div>
                <div className="rounded-full bg-neutral-25 px-2 py-1 text-caption font-medium text-neutral-125">
                  {authedProviderUser.provider.seatUsage.usedClientSeats} /{' '}
                  {authedProviderUser.provider.seatUsage.clientSeats}
                </div>
              </div>
            )}
            {!isInArchivedMode && (
              <Button
                title="Add clients"
                IconComponent={PlusSmIcon}
                iconPosition="left"
                onClick={addPatients}
              />
            )}
          </div>
        </div>
        <div className="mb-4 flex h-[56px] w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center justify-start">
            <InputGroup
              autoFocus
              labelHidden
              label="Search"
              placeholder="Search..."
              className="h-[44px] w-80 rounded-full pl-12 disabled:cursor-not-allowed"
              containerClassName="mr-4"
              iconLeftClassName="text-neutral-125 ml-2"
              IconLeft={Search}
              IconRight={searchTermValue.length > 0 ? CloseX : null}
              iconRightOnClick={() => {
                setSearchTermValue('');
                dispatch({
                  type: PatientManagementActionType.SetSearchTerm,
                  payload: '',
                });
                clearSelection();
              }}
              iconRightClassName="text-neutral-125 mr-1"
              value={searchTermValue}
              onChange={(e) => {
                setSearchTermValue(e.target.value);
                debouncedSearch(e);
                clearSelection();
              }}
            />
            <div className="flex flex-row items-center justify-start gap-x-4 md:gap-x-5">
              <div>
                <CombinedFilter
                  availableProviderUsers={availableProviderUsersFromTeam}
                  availableTags={providerProgramTags}
                  selectedProviderUserIds={providerUserFilterIds}
                  selectedTagIds={tagFilterIds}
                  selectedExcludedTagIds={excludedTagFilterIds}
                  selectedTagsMatch={tagsMatch}
                  selectedProvidersMatch={providersMatch}
                  selectedFilterOperator={filterOperator}
                  selectedNoAssignedPractitioners={noAssignedPractitioners}
                  selectedNoTags={noTags}
                  onApply={handleCombinedFilterApply}
                  onClear={() => clearFilters()}
                />
              </div>

              {/* Clear filters */}
              {(providerUserFilterIds.length > 0 ||
                tagFilterIds.length > 0 ||
                excludedTagFilterIds.length > 0 ||
                noAssignedPractitioners ||
                noTags) && (
                <UpdateButton
                  onClick={handleClearFilters}
                  IconComponent={XIcon}
                  theme="red"
                  text="Clear"
                  ariaLabel="Clear filters"
                />
              )}

              {/* Column Visibility Settings - only for Active clients */}
              {!isInArchivedMode &&
                (patientLifecycleStatus === PatientLifecycleStatusV2.Active ||
                  showManageTags ||
                  showExportClients) && (
                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button as={Fragment}>
                      <Button
                        title="Tools"
                        theme="secondary"
                        size="small"
                        IconComponent={CogIcon}
                        iconPosition="left"
                      />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute left-0 z-20 mt-2 w-64 origin-top-left rounded-lg bg-white shadow-lg focus:outline-none">
                        <div className="p-0">
                          {patientLifecycleStatus ===
                            PatientLifecycleStatusV2.Active && (
                            <MenuItemButton
                              titleText="Column visibility"
                              IconComponent={EyeIcon}
                              iconClassName="text-secondary-150 h-[12px] w-[12px]"
                              className="rounded-t-md border-b border-neutral-100 text-secondary-150"
                              activeText="font-medium text-secondary-150"
                              onClick={() => {
                                if (setColumnVisibilityOpen) {
                                  setColumnVisibilityOpen();
                                }
                              }}
                            />
                          )}
                          {showManageTags && (
                            <MenuItemButton
                              titleText="Tag management"
                              IconComponent={Tag}
                              iconClassName="text-secondary-150 h-[12px] w-[12px]"
                              className={classNames(
                                showExportClients && !isInArchivedMode
                                  ? 'border-b border-neutral-100 text-secondary-150'
                                  : 'rounded-b-md',
                              )}
                              activeText="font-medium text-secondary-150"
                              onClick={() => setIsTagManagerModalOpen(true)}
                            />
                          )}
                          {showExportClients && !isInArchivedMode && (
                            <MenuItemButton
                              titleText="Export list"
                              IconComponent={DownloadIcon}
                              iconClassName="text-secondary-150 h-[12px] w-[12px]"
                              className="rounded-b-md"
                              activeText="font-medium text-secondary-150"
                              onClick={handleExportFromMenu}
                            />
                          )}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}
              {/* Hidden column modal trigger to expose open function */}
              {patientLifecycleStatus === PatientLifecycleStatusV2.Active && (
                <div className="hidden">
                  <ColumnVisibilitySettings
                    availableColumns={columnHeaders.active}
                    visibleColumns={visibleColumns}
                    onColumnVisibilityChange={handleColumnVisibilityChange}
                    visibleActions={visibleActions}
                    onVisibleActionsChange={handleVisibleActionsChange}
                    actionsSubOptions={ACTIONS_SUB_OPTIONS}
                    hideTriggerButton
                    exposeOpen={(open) =>
                      setSetColumnVisibilityOpen(() => open)
                    }
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center justify-end">
            <button
              className={classNames(
                'flex flex-row items-center justify-start rounded-full pl-3.5 pr-2 text-caption text-neutral-125',
                isInActiveMode ? 'bg-neutral-50' : 'hover:bg-neutral-25',
              )}
              onClick={handleActiveClick}
            >
              <div>Active</div>
              <div className="ml-2 rounded-full bg-neutral-50 px-2.5 py-1 font-medium">
                {activeCountLabel}
              </div>
            </button>
            <button
              className={classNames(
                'ml-3 flex flex-row items-center justify-start rounded-full pl-3.5 pr-2 text-caption text-neutral-125',
                isInArchivedMode ? 'bg-neutral-50' : 'hover:bg-neutral-25',
              )}
              onClick={handleArchivedClick}
            >
              <ArchiveIcon className="mr-2 h-5 w-5 text-neutral-125" />
              <div>Archived</div>
              <div className="ml-2 rounded-full bg-neutral-50 px-2.5 py-1 font-medium">
                {archivedCountLabel}
              </div>
            </button>
          </div>
        </div>
        <BottomButtons
          isVisible={isInActiveMode && selectedPatientsList.length > 0}
          actions={[
            {
              title: `Add tag (${selectedPatientsList.length} selected)`,
              onClick: () => setIsBulkAddTagModalOpen(true),
            },
            {
              title: `Remove tag (${selectedPatientsList.length} selected)`,
              onClick: () => setIsBulkRemoveTagModalOpen(true),
              theme: 'destructive',
              className: '!rounded-none border-l border-neutral-50',
              disabled: !hasTagsToRemove,
            },
            {
              title: 'Clear selection',
              theme: 'secondary',
              onClick: clearSelection,
            },
          ]}
        />
        <TableContainer>
          <Outlet
            context={{
              nextStepsButtonsDisabled: true,
              patientLifecycleStatus,
              hasPatientsList,
              patientsCount,
              tableLoading,
              patientOrder: sortOrder,
              patientsList: filteredPatientsList,
              restrictedPatientsList: filteredRestrictedPatientsList,
              clearFilters,
              handleSortOrderChange,
              setSinglePatient,
              setIsSendAssessmentModalOpen,
              addPatients,
              openResendInvitesModal: () => setIsResendInvitesModalOpen(true),
              providerProgramTags,
              refetchMeProviderProgramTags,
              handleProgramTagsUpdate,
              handleUnarchiveClick,
              handleAddStorefrontClient,
              visibleColumns: visibleColumns,
              visibleActions: visibleActions,
              selectedPatientsObject,
              selectedPatientsList,
              handleRowCheckboxChange,
              handleSelectAll,
              clearSelection,
              setLastSelectedPatientIndex,
            }}
          />
        </TableContainer>
        <div
          ref={sentinelRef}
          className={classNames(
            `flex h-24 w-full items-center justify-center`,
            morePatientsLoading && !tableLoading ? 'opacity-100' : 'opacity-0',
          )}
        >
          <Spinner className="h-4 w-4" />
        </div>
      </div>

      <AddPatientsModal
        patientsObject={patientsObject}
        isModalOpen={isAddPatientsModalOpen}
        setPatientsObject={setPatientsObject}
        setClosed={() => setIsAddPatientsModalOpen(false)}
        clearFilters={clearFilters}
        providerProgramTags={providerProgramTags}
        refetchMeProviderProgramTags={refetchMeProviderProgramTags}
        noPatientDataPermission={noPatientDataPermission}
        refetchPatientsCount={refetchPatientsCount}
      />
      <ResendInvitesModal
        isPatientManagement
        patientsObject={patientsObject}
        singlePatient={singlePatient}
        isModalOpen={isResendInvitesModalOpen}
        patientLifecycleStatus={patientLifecycleStatus}
        setClosed={() => setIsResendInvitesModalOpen(false)}
        setPatientsObject={setPatientsObject}
        clearFilters={clearFilters}
      />
      <TagManagerModal
        isModalOpen={isTagManagerModalOpen}
        setClosed={() => setIsTagManagerModalOpen(false)}
        initialProviderProgramTags={providerProgramTags}
        onClose={async () => {
          await refetchMeProviderProgramTags();
          await refetchPatientsTable();
        }}
      />
      <UpgradeModal
        isModalOpen={isBlockingUpgradeModalOpen}
        setClosed={() => {
          setIsBlockingUpgradeModalOpen(false);
        }}
        originPage={AnalyticsPage.ClientsTable}
        isBlocking
        handleSelectActiveClients={() => {
          setIsTrialEndDeactivatePatientsModalOpen(true);
        }}
      />
      <TrialEndDeactivatePatientsModal
        isOpen={isTrialEndDeactivatePatientsModalOpen}
        setClosed={() => setIsTrialEndDeactivatePatientsModalOpen(false)}
        patientsObject={patientsObject}
        onDeactivation={handlePostDeactivation}
      />
      <BulkAddTagModal
        isOpen={isBulkAddTagModalOpen}
        onClose={() => setIsBulkAddTagModalOpen(false)}
        selectedPatients={selectedPatientsList}
        providerProgramTags={providerProgramTags}
        refetchMeProviderProgramTags={refetchMeProviderProgramTags}
        onSuccess={async () => {
          await refetchPatientsTable();
          clearSelection();
        }}
      />
      <BulkRemoveTagModal
        isOpen={isBulkRemoveTagModalOpen}
        onClose={() => setIsBulkRemoveTagModalOpen(false)}
        selectedPatients={selectedPatientsList}
        providerProgramTags={providerProgramTags}
        onSuccess={async () => {
          await refetchPatientsTable();
          clearSelection();
        }}
      />
    </PageContainer>
  );
};

export default PatientManagement;
